import {ButtonProps, closeDialog, openDialog, showNotification} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {TenantResponseBody} from '@dms/api/shared';
import {
  GetWarehouseResponse,
  PostReceiveNoteBasketItemRequest,
  ReceiveNoteBasketItem,
  useBulkDeleteReceiveNoteBasketItemsMutation,
  useDeleteReceiveNoteBasketMutation,
  useGetReceiveNoteBasketQuery,
  usePatchReceiveNoteBasketItemQuantityMutation,
  usePatchReceiveNoteItemManualMutation,
  usePostReceiveNoteBasketCheckoutMutation,
  usePostReceiveNoteBasketItemMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  AfterSalesMaterialBasket,
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  logError,
  MaterialBasketItemEditingDetails,
  useInvalidBasketItemsIds,
  usePermissions,
} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {RECEIVE_NOTE_ITEM_DIALOG} from '../constants/receiveNoteItemDialog';
import {ItemRowData} from '../types/ItemRowData';
import {ReceiveNoteItemDetail} from './ReceiveNoteItemDetail';
import {ReceiveNoteMaterialList} from './ReceiveNoteMaterialList';

interface ReceiveNoteMaterialModalProps extends RequiredTestIdProps {
  receiveNoteId: string;
  authorizationProfileId: string | Nullish;
  receiveNoteSupplierId: string | Nullish;
  branchId: string | Nullish;
  warehouse: GetWarehouseResponse | Nullish;
  tenant: TenantResponseBody | Nullish;
  state: 'PENDING' | 'COMPLETED' | Nullish;
  onDiscard: VoidFunction;
}

export function ReceiveNoteMaterialModal(props: ReceiveNoteMaterialModalProps) {
  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetReceiveNoteBasketQuery({receiveNoteId: props.receiveNoteId});

  const [checkoutBasket, {isLoading: isCheckingOutBasket}] =
    usePostReceiveNoteBasketCheckoutMutation();

  const [discardBasket, {isLoading: isDiscardingBasket}] = useDeleteReceiveNoteBasketMutation();

  const [postReceiveNoteBasketItem, {isLoading: isAddingToBasket}] =
    usePostReceiveNoteBasketItemMutation();

  const [changeBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchReceiveNoteBasketItemQuantityMutation();

  const [deleteBasketItems, {isLoading: isDeletingBasketItems}] =
    useBulkDeleteReceiveNoteBasketItemsMutation();

  const [, {isLoading: isEditingReceiveNoteItem}] = usePatchReceiveNoteItemManualMutation({
    fixedCacheKey: 'edit-receive-note-item',
  });

  const basketItems = basket?.receiveNoteBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basket?.receiveNoteBasketTotalPrice;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingToBasket ||
    isDiscardingBasket ||
    isCheckingOutBasket ||
    isChangingQuantity ||
    isEditingReceiveNoteItem ||
    isDeletingBasketItems;

  const {setInvalidBasketItemId, hasInvalidBasketItems} = useInvalidBasketItemsIds(basketItems);

  const isCheckoutDisabled = not(canEditReceiveNote) || isBasketEmpty || hasInvalidBasketItems;

  const isDiscardDisabled = not(canEditReceiveNote);

  const handleDiscard = () =>
    discardBasket({receiveNoteId: props.receiveNoteId})
      .unwrap()
      .then(props.onDiscard)
      .catch(handleApiError);

  const handleCheckout = () =>
    checkoutBasket({
      receiveNoteId: props.receiveNoteId,
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.itemsAdded')))
      .then(props.onDiscard)
      .catch(handleApiError);

  const handleAddToBasket = async (requestBody: PostReceiveNoteBasketItemRequest['body']) => {
    await postReceiveNoteBasketItem({body: requestBody})
      .unwrap()
      .then(() => closeDialog(RECEIVE_NOTE_ITEM_DIALOG))
      .catch(handleApiError);
  };

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setInvalidBasketItemId(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await changeBasketItemQuantity({
      receiveNoteId: props.receiveNoteId,
      itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEdit = (editingDetails: MaterialBasketItemEditingDetails) => {
    if (isNil(editingDetails?.itemId)) {
      return logError('itemId is not defined');
    }

    if (isNil(editingDetails?.warehouseId)) {
      return logError('warehouseId is not defined');
    }

    if (isNil(editingDetails?.manufacturerId)) {
      return logError('manufacturerId is not defined');
    }

    const itemRowData: ItemRowData = {
      warehouseId: editingDetails.warehouseId,
      articleId: editingDetails.itemId,
      catalogNumber: editingDetails.itemNumber,
      name: editingDetails.itemName,
      manufacturerId: editingDetails.manufacturerId,
      itemId: editingDetails.itemId,
    };

    openDialog(
      <ReceiveNoteItemDetail
        mode="editing"
        authorizationProfileId={props.authorizationProfileId}
        receiveNoteId={props.receiveNoteId}
        receiveNoteSupplierId={props.receiveNoteSupplierId}
        receiveNoteItemId={editingDetails.itemId}
        itemRowData={itemRowData}
        warehouse={props.warehouse}
        tenant={props.tenant}
        state={props.state}
        data-testid={suffixTestId('dialog.editMaterial', props)}
      />,
      {
        id: RECEIVE_NOTE_ITEM_DIALOG,
        title: i18n.t('general.labels.edit'),
        size: 'large',
        withAdditionalFooter: true,
        'data-testid': suffixTestId('dialogs.editItem', props),
      }
    );
  };

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody = {receiveNoteItemId: ids.map((id) => id)};

    await deleteBasketItems({
      receiveNoteId: props.receiveNoteId,
      body: requestBody,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const actions: ButtonProps[] = [
    {
      isLoading: hasPendingRequest,
      isDisabled: isDiscardDisabled,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
      'data-testid': suffixTestId('headerActions.discard', props),
    },
    {
      isLoading: hasPendingRequest,
      isDisabled: isCheckoutDisabled,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  return (
    <FullScreenModal
      headline={i18n.t('general.actions.addMaterial')}
      actions={actions}
      data-testid={props['data-testid']}
    >
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <ReceiveNoteMaterialList
              receiveNoteId={props.receiveNoteId}
              authorizationProfileId={props.authorizationProfileId}
              receiveNoteSupplierId={props.receiveNoteSupplierId}
              branchId={props.branchId}
              warehouse={props.warehouse}
              tenant={props.tenant}
              state={props.state}
              onAddToBasket={handleAddToBasket}
              isAddingToBasket={isAddingToBasket}
              hasPendingRequest={hasPendingRequest}
              data-testid={suffixTestId('itemList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesMaterialBasket<ReceiveNoteBasketItem>
              basket={{
                items: basketItems,
                totalPrice: basketTotalPrice,
                isLoading: isBasketLoading,
                hasError: hasBasketError,
                hasPendingRequest,
                hasInvalidItems: hasInvalidBasketItems,
              }}
              mechanic={{isMechanicHidden: true}}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
